// src/pages/SecurityStatus.jsx
//
// 「いまサーバーは安全か」を1画面で見るためのページ。
//
// ── 作りの方針 ────────────────────────────────────────────────
// 判定（何が危なくて、どうすればいいか）は全部サーバー側の
// services/securityStatusService.js が決めている。この画面は
// 受け取ったものを色と文章で並べるだけ。
// しきい値や助言を直したいときにフロントを再ビルドしなくて済む。
//
// ── 色の扱い ──────────────────────────────────────────────────
// 状態は「色」だけでは伝えない。必ず 記号 + 文字ラベル と一緒に出す。
// 色覚特性や、印刷・モノクロ表示でも意味が伝わるようにするため。
import React, { useEffect, useState, useCallback } from 'react';

// 状態の見た目。色は補助であって、本体は label と記号。
const STATE = {
  ok:      { label: '良好',   color: '#0ca30c', bg: '#eaf7ea', mark: '✓' },
  warn:    { label: '確認',   color: '#b07600', bg: '#fdf5e3', mark: '!' },
  danger:  { label: '要対応', color: '#d03b3b', bg: '#fbecec', mark: '×' },
  unknown: { label: '未設定', color: '#6b7280', bg: '#f3f4f6', mark: '?' }
};

const GROUP_DESC = {
  '入口':     '外から入られないようにする部分',
  '中身':     '入られた場合に、何を持ち出されるか',
  '最後の砦': '破られたときに、戻せるか・気づけるか',
  '設定':     'うっかりで止まらないための基本設定'
};

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

/** 状態バッジ（色 + 記号 + 文字） */
function Badge({ status, big }) {
  const s = STATE[status] || STATE.unknown;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      background: s.bg, color: s.color,
      border: `1px solid ${s.color}33`,
      borderRadius: 999,
      padding: big ? '0.35rem 0.9rem' : '0.15rem 0.6rem',
      fontSize: big ? '0.95rem' : '0.78rem',
      fontWeight: 700, whiteSpace: 'nowrap'
    }}>
      <span aria-hidden="true" style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: big ? 20 : 16, height: big ? 20 : 16, borderRadius: '50%',
        background: s.color, color: '#fff',
        fontSize: big ? '0.8rem' : '0.65rem', fontWeight: 900, lineHeight: 1
      }}>{s.mark}</span>
      {s.label}
    </span>
  );
}

/** 「こうしてください」の欄。コマンドをそのままコピーできるように */
function HowTo({ text }) {
  if (!text) return null;
  const hasCommand = /\n|psql |node |sudo |pm2 /.test(text);
  return (
    <div style={{
      marginTop: '0.6rem', padding: '0.6rem 0.75rem',
      background: '#f8fafc', border: '1px solid #e2e8f0',
      borderLeft: '3px solid #94a3b8', borderRadius: 6
    }}>
      <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, marginBottom: '0.3rem' }}>
        どうすればいい？
      </div>
      <div style={{
        fontSize: '0.82rem', color: '#334155', whiteSpace: 'pre-wrap',
        fontFamily: hasCommand ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
        lineHeight: 1.65, wordBreak: 'break-word'
      }}>{text}</div>
    </div>
  );
}

function CheckRow({ check }) {
  const s = STATE[check.status] || STATE.unknown;
  return (
    <div style={{
      padding: '0.9rem 0',
      borderTop: '1px solid #f1f5f9'
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap'
      }}>
        <Badge status={check.status} />
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>
            {check.label}
          </div>
          <div style={{
            color: s.color, fontWeight: 700, fontSize: '1.05rem', marginTop: '0.15rem'
          }}>
            {check.value}
          </div>
          <div style={{
            color: '#4b5563', fontSize: '0.85rem', marginTop: '0.35rem', lineHeight: 1.7
          }}>
            {check.detail}
          </div>

          {Array.isArray(check.findings) && check.findings.length > 0 && (
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem', color: '#b07600', fontSize: '0.85rem' }}>
              {check.findings.map((f, i) => <li key={i} style={{ marginBottom: '0.2rem' }}>{f}</li>)}
            </ul>
          )}

          <HowTo text={check.howto} />
        </div>
      </div>
    </div>
  );
}

export default function SecurityStatus({ user }) {
  const [ov, setOv]           = useState(null);
  const [locked, setLocked]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [busyId, setBusyId]   = useState('');
  const [msg, setMsg]         = useState('');

  // ロック解除は教務スタッフ以上だけ（サーバー側でも同じ制限をかけている）
  const canUnlock = user && ['academic_staff', 'developer'].includes(user.role);

  const load = useCallback(async () => {
    setErr('');
    try {
      const res  = await fetch('/api/admin/security/overview', { headers: auth() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '取得に失敗しました');
      setOv(data.overview);

      if (canUnlock) {
        const r2 = await fetch('/api/admin/security/locked-accounts', { headers: auth() });
        const d2 = await r2.json();
        if (d2.success) setLocked(d2.accounts || []);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [canUnlock]);

  useEffect(() => {
    load();
    // 開きっぱなしにしても腐らないよう5分ごとに更新する
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const unlock = async (loginId) => {
    setBusyId(loginId); setMsg('');
    try {
      const res = await fetch('/api/admin/security/unlock', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId: loginId })
      });
      const data = await res.json();
      setMsg(data.message || (data.success ? '解除しました' : '解除できませんでした'));
      await load();
    } catch (e) {
      setMsg('解除に失敗しました: ' + e.message);
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>サーバーの安全確認</h1></div>
        <div className="card" style={{ color: '#6b7280' }}>読み込み中...</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>サーバーの安全確認</h1></div>
        <div className="card" style={{ borderLeft: '4px solid #d03b3b' }}>
          <div style={{ color: '#d03b3b', fontWeight: 700, marginBottom: '0.5rem' }}>
            状態を取得できませんでした
          </div>
          <div style={{ color: '#4b5563', fontSize: '0.9rem' }}>{err}</div>
          <button className="btn-secondary" style={{ marginTop: '0.9rem' }} onClick={load}>
            もう一度試す
          </button>
        </div>
      </div>
    );
  }

  const s = STATE[ov.overall] || STATE.unknown;
  const groups = [...new Set(ov.checks.map(c => c.group))];
  const needAction = ov.checks.filter(c => c.status === 'danger' || c.status === 'unknown');

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>サーバーの安全確認</h1>
        <p>専門用語なしで、いまの状態と「次に何をすればいいか」だけを並べています。</p>
      </div>

      {/* ── 総合判定 ─────────────────────────────── */}
      <div className="card" style={{ borderLeft: `6px solid ${s.color}`, background: s.bg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <Badge status={ov.overall} big />
          <div style={{ flex: '1 1 260px' }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#111827' }}>
              {ov.summary}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#4b5563', marginTop: '0.3rem' }}>
              良好 {ov.counts.ok} ／ 確認 {ov.counts.warn} ／ 要対応 {ov.counts.danger} ／ 未設定 {ov.counts.unknown}
              {'　'}·{'　'}
              {new Date(ov.checkedAt).toLocaleString('ja-JP')} 時点
            </div>
          </div>
          <button className="btn-secondary" onClick={load}>最新にする</button>
        </div>

        {needAction.length > 0 && (
          <div style={{
            marginTop: '1rem', paddingTop: '0.9rem', borderTop: `1px solid ${s.color}33`
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827', marginBottom: '0.4rem' }}>
              まずここから
            </div>
            <ol style={{ margin: 0, paddingLeft: '1.2rem', color: '#374151', fontSize: '0.88rem', lineHeight: 1.9 }}>
              {needAction.map(c => <li key={c.id}>{c.label} — {c.value}</li>)}
            </ol>
          </div>
        )}
      </div>

      {/* ── 項目ごと ─────────────────────────────── */}
      {groups.map(g => (
        <div className="card" key={g}>
          <div className="card-header">
            <h2 style={{ margin: 0 }}>{g}</h2>
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {GROUP_DESC[g] || ''}
            </div>
          </div>
          {ov.checks.filter(c => c.group === g).map(c => <CheckRow key={c.id} check={c} />)}
        </div>
      ))}

      {/* ── ロック中のアカウント ───────────────────── */}
      {canUnlock && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ margin: 0 }}>ロック中のアカウント</h2>
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.25rem' }}>
              パスワードを続けて間違えると自動でロックされます。本人確認のうえ、ここで解除できます。
            </div>
          </div>

          {msg && (
            <div style={{
              marginBottom: '0.9rem', padding: '0.6rem 0.8rem', borderRadius: 6,
              background: '#eff6ff', color: '#1e40af', fontSize: '0.85rem'
            }}>{msg}</div>
          )}

          {!locked || locked.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              いまロックされているアカウントはありません。
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ログインID</th>
                    <th>氏名</th>
                    <th>失敗</th>
                    <th>解除予定</th>
                    <th>最終アクセス元</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {locked.map(a => (
                    <tr key={a.loginId}>
                      <td style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                        {a.loginId}
                      </td>
                      <td>
                        {a.exists ? (a.name || '—') : (
                          <span style={{ color: '#d03b3b', fontWeight: 700 }}>
                            存在しないID
                          </span>
                        )}
                      </td>
                      <td>{a.failCount}回</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {new Date(a.lockedUntil).toLocaleString('ja-JP')}
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{a.lastIp || '—'}</td>
                      <td>
                        <button
                          className="btn-secondary"
                          disabled={busyId === a.loginId}
                          onClick={() => unlock(a.loginId)}
                        >
                          {busyId === a.loginId ? '解除中...' : '解除'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {locked && locked.some(a => !a.exists) && (
            <div style={{
              marginTop: '0.9rem', padding: '0.7rem 0.85rem', borderRadius: 6,
              background: '#fbecec', color: '#991b1b', fontSize: '0.85rem', lineHeight: 1.7
            }}>
              <strong>「存在しないID」が並んでいます。</strong>
              実在しない学籍番号で総当たりされている合図です。1〜2件なら打ち間違いですが、
              まとまって出ているなら攻撃を受けています。防御は効いているので慌てる必要はありませんが、
              「昨日1日のログイン失敗」の数も合わせて見てください。
            </div>
          )}
        </div>
      )}

      {/* ── サーバーの基本情報 ─────────────────────── */}
      <div className="card">
        <div className="card-header"><h2 style={{ margin: 0 }}>サーバーの状態</h2></div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">連続稼働</div>
            <div className="stat-value">
              {Math.floor(ov.server.uptimeSec / 86400)}
              <span className="stat-unit">日</span>
              {Math.floor((ov.server.uptimeSec % 86400) / 3600)}
              <span className="stat-unit">時間</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">動作モード</div>
            <div className="stat-value" style={{ fontSize: '1.2rem' }}>
              {ov.server.env === 'production' ? '本番' : ov.server.env}
            </div>
          </div>
          {ov.server.db && (
            <div className="stat-card">
              <div className="stat-label">DB接続（待ち）</div>
              <div className="stat-value">
                {ov.server.db.total}<span className="stat-unit">/{ov.server.db.max}</span>
                {ov.server.db.waiting > 0 && (
                  <span style={{ color: '#b07600', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                    待ち{ov.server.db.waiting}
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-label">Node.js</div>
            <div className="stat-value" style={{ fontSize: '1.2rem' }}>{ov.server.nodeVersion}</div>
          </div>
        </div>
      </div>

      <div style={{ color: '#9ca3af', fontSize: '0.78rem', lineHeight: 1.8, marginBottom: '2rem' }}>
        この画面は5分ごとに自動で更新されます。<br />
        詳しい背景と手順は、リポジトリの SECURITY-HARDENING.md にまとめてあります。
      </div>
    </div>
  );
}
